import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import PropTypes from 'prop-types';
import apiService from '../providers/ApiService';

export default function UserProfileComponent({ userEmail }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [completed, setCompleted] = useState([]); // Initialize as an array, not an object
  const [dobYear, setDobYear] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Gender options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];

  // Date of birth options (years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i - 18); // Ages 18-117
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Fetch user data function that can be called after saving
  const fetchUserProfile = async () => {
    try {
      const data = await apiService.get(`/user/${userEmail}`);

      if (data) {
        setUserData(data);

        // Set date of birth parts if available
        if (data.dob) {
          const dobDate = new Date(data.dob);
          setDobYear(dobDate.getFullYear().toString());
          setDobMonth((dobDate.getMonth() + 1).toString());
          setDobDay(dobDate.getDate().toString());
        }

        // Set completed cases from data
        setCompleted(data.completed || []);

        // Set initial edited data
        setEditedData({
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender || '',
          tier: data.tier || '',
          status: data.status || '',
          subscriptionExpiry: data.subscriptionExpiry,
          customerId: data.customerId || ''
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [userEmail]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedData(userData); // Reset edited data to original

    // Reset DOB parts
    if (userData.dob) {
      const dateParts = userData.dob.split('-');
      if (dateParts.length === 3) {
        setDobYear(dateParts[0]);
        setDobMonth(dateParts[1]);
        setDobDay(dateParts[2]);
      }
    }
  };

  const handleInputChange = (e) => {
    setEditedData({
      ...editedData,
      [e.target.name]: e.target.value
    });
  };

  const handleDobChange = (part, value) => {
    let newYear = part === 'year' ? value : dobYear;
    let newMonth = part === 'month' ? value : dobMonth;
    let newDay = part === 'day' ? value : dobDay;

    // Update the state
    if (part === 'year') setDobYear(value);
    if (part === 'month') setDobMonth(value);
    if (part === 'day') setDobDay(value);

    // Only update dob if we have all parts
    if (newYear && newMonth && newDay) {
      setEditedData({
        ...editedData,
        dob: `${newYear}-${newMonth}-${newDay}`
      });
    }
  };

  const handleSaveClick = async () => {
    setSaving(true);

    // Format the new date of birth if all parts are provided
    let formattedDob = null;
    if (dobYear && dobMonth && dobDay) {
      const month = parseInt(dobMonth, 10);
      const day = parseInt(dobDay, 10);
      formattedDob = `${dobYear}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
    }

    const updatedUserData = {
      ...userData,
      ...editedData,
      dob: formattedDob || userData.dob
    };

    try {
      await apiService.put(`/user/${userEmail}`, updatedUserData);

      setUserData(updatedUserData);
      setIsEditing(false);
      // toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      // toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await apiService.get(`/user/cancel/${userEmail}`);
      console.log('Cancellation response:', response);

      // Directly update the userData with status changed to cancelled
      // This gives immediate feedback while we fetch the full update
      setUserData((prevData) => ({
        ...prevData,
        status: 'cancelled'
      }));

      // Close the dialog first for better UX
      setCancelDialogOpen(false);

      // Wait for a few seconds to allow the webhook to process
      // This is a better approach than polling repeatedly
      setTimeout(async () => {
        try {
          // Then fetch fresh data from the server after a delay
          const updatedData = await apiService.get(`/user/${userEmail}`);
          console.log('Updated user data after cancellation:', updatedData);

          if (updatedData) {
            // Update all state variables with new data
            setUserData(updatedData);
            setCompleted(Array.isArray(updatedData.completed) ? updatedData.completed : []);
          }
        } catch (fetchError) {
          console.error('Error fetching updated user data:', fetchError);
        } finally {
          setCancelling(false);
        }
      }, 5000); // 5 second delay - adjust as needed based on your backend response time
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setCancelling(false);
    }
  };

  // Helper function to get days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Helper function to format the date for display
  const formatDateOfBirth = (dobString) => {
    if (!dobString) return '';

    try {
      const [year, month, day] = dobString.split('-');
      const monthName = months[parseInt(month) - 1];
      return `${day} ${monthName} ${year}`;
    } catch (error) {
      return dobString;
    }
  };

  // Helper function to format subscription expiry date
  const formatSubscriptionExpiry = (expiryDateString) => {
    if (!expiryDateString) return 'No active subscription';

    try {
      const expiryDate = new Date(expiryDateString);
      return expiryDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return expiryDateString;
    }
  };

  // Helper function to determine if subscription is active
  const isSubscriptionActive = () => {
    if (userData?.status === 'cancelled') return false;
    if (!userData?.subscriptionExpiry) return false;

    try {
      const expiryDate = new Date(userData.subscriptionExpiry);
      return expiryDate > new Date();
    } catch (error) {
      return false;
    }
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5, pt: { xs: 12, sm: 12 } }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading profile...
        </Typography>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5, pt: { xs: 12, sm: 12 } }}>
        <Typography variant="h6" color="error">
          Failed to load user profile.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: { xs: 12, sm: 12 }, pb: { xs: 8, sm: 16 }, mt: 5 }}>
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        {isEditing ? (
          <>
            <TextField
              label="First Name"
              name="firstName"
              value={editedData.firstName || ''}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={editedData.lastName || ''}
              onChange={handleInputChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                name="gender"
                value={editedData.gender || ''}
                label="Gender"
                onChange={handleInputChange}>
                {genderOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'left' }}>
              Date of Birth
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="dob-year-label">Year</InputLabel>
                <Select
                  labelId="dob-year-label"
                  id="dob-year"
                  value={dobYear || ''}
                  label="Year"
                  onChange={(e) => handleDobChange('year', e.target.value)}>
                  {years.map((year) => (
                    <MenuItem key={year} value={year.toString()}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="dob-month-label">Month</InputLabel>
                <Select
                  labelId="dob-month-label"
                  id="dob-month"
                  value={dobMonth || ''}
                  label="Month"
                  onChange={(e) => handleDobChange('month', e.target.value)}>
                  {months.map((month, index) => (
                    <MenuItem key={month} value={(index + 1).toString().padStart(2, '0')}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="dob-day-label">Day</InputLabel>
                <Select
                  labelId="dob-day-label"
                  id="dob-day"
                  value={dobDay || ''}
                  label="Day"
                  onChange={(e) => handleDobChange('day', e.target.value)}>
                  {dobYear && dobMonth
                    ? Array.from(
                        { length: getDaysInMonth(dobYear, parseInt(dobMonth)) },
                        (_, i) => i + 1
                      ).map((day) => (
                        <MenuItem key={day} value={day.toString().padStart(2, '0')}>
                          {day}
                        </MenuItem>
                      ))
                    : days.map((day) => (
                        <MenuItem key={day} value={day.toString().padStart(2, '0')}>
                          {day}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Tier Level"
              name="tier"
              value={editedData.tier || ''}
              fullWidth
              sx={{ mb: 2 }}
              disabled
              InputProps={{
                readOnly: true
              }}
            />
            <TextField
              label="Subscription Expiry"
              name="subscriptionExpiry"
              value={formatSubscriptionExpiry(editedData.subscriptionExpiry || '')}
              fullWidth
              sx={{ mb: 2 }}
              disabled
              InputProps={{
                readOnly: true
              }}
            />
            <TextField
              label="Subscription Status"
              name="status"
              value={editedData.status === 'cancelled' ? 'Cancelled' : 'Active'}
              fullWidth
              sx={{ mb: 2 }}
              disabled
              InputProps={{
                readOnly: true
              }}
            />
            <TextField
              label="Customer ID"
              name="customerId"
              value={editedData.customerId || 'Not available'}
              fullWidth
              sx={{ mb: 2 }}
              disabled
              InputProps={{
                readOnly: true
              }}
            />
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={saving}>
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
              </Button>
              <Button
                variant="outlined"
                sx={{ ml: 2 }}
                onClick={handleCancelClick}
                disabled={saving}>
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom>
              {userData.firstName || ''} {userData.lastName || ''}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {userData.userId || ''}
            </Typography>

            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="body1">
                <strong>Gender:</strong> {userData.gender || 'Not specified'}
              </Typography>
              <Typography variant="body1">
                <strong>Date of Birth:</strong> {formatDateOfBirth(userData.dob) || 'Not specified'}
              </Typography>
              <Typography variant="body1">
                <strong>Tier Level:</strong> {userData.tier || 'Not assigned'}
              </Typography>
              <Typography variant="body1">
                <strong>Subscription Expiry:</strong>{' '}
                {formatSubscriptionExpiry(userData.subscriptionExpiry)}
              </Typography>
              <Typography variant="body1">
                <strong>Subscription Status:</strong>{' '}
                {userData.status === 'cancelled' ? 'Cancelled' : 'Active'}
              </Typography>
              <Typography variant="body1">
                <strong>Customer ID:</strong> {userData.customerId || 'Not available'}
              </Typography>
              {/* <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Completed Cases:</strong>
              </Typography>
              {completed?.length > 0 ? (
                <List>
                  {completed.map((caseId, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={caseId} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No cases completed yet.
                </Typography>
              )} */}
            </Box>
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center'
              }}>
              <Button variant="contained" color="primary" onClick={handleEditClick}>
                Edit Profile
              </Button>

              {userData.subscriptionExpiry && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={userData.status === 'cancelled' || cancelling}>
                  {userData.status === 'cancelled'
                    ? 'Subscription Cancelled'
                    : 'Cancel Subscription'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Confirmation Dialog for Subscription Cancellation */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description">
        <DialogTitle id="cancel-dialog-title">Cancel Subscription</DialogTitle>
        <DialogContent>
          {/* <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel your subscription? You will continue to have access
            until your current subscription period ends on{' '}
            {formatSubscriptionExpiry(userData?.subscriptionExpiry)}.
          </DialogContentText> */}
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel your subscription? You will continue to have access
            until your current subscription period ends on{' '}
            {formatSubscriptionExpiry(userData?.subscriptionExpiry)}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="primary">
            Keep Subscription
          </Button>
          <Button onClick={handleCancelSubscription} color="error" disabled={cancelling}>
            {cancelling ? <CircularProgress size={24} /> : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

UserProfileComponent.propTypes = {
  userEmail: PropTypes.string.isRequired
};
